import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
  InputBase,
  Slide,
  useMediaQuery,
  useTheme,
  Container,
  Collapse,
  Menu,
  MenuItem,
  Divider,
  Paper,
  ClickAwayListener,
  CircularProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import MenuIcon from "@mui/icons-material/Menu";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { postData } from "../../Webservice/Webservice";
import { useCart } from "../../context/CartContext";
import { getUser, getToken, logout } from "../../Utils/authHelper";

const rubikfont = {
  fontFamily: "Rubik, sans-serif"
};

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const hoverTimeoutRef = useRef(null);

  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [user, setUser] = useState(null);
  const { cartCount } = useCart();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  const [clusters, setClusters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [unclusteredCategories, setUnclusteredCategories] = useState([]);
  const [activeMobileCluster, setActiveMobileCluster] = useState(null);
  const [activeMobileCategory, setActiveMobileCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Search dropdown states
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleMouseEnter = (id) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setOpenDropdown(id);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tokenValue = getToken();
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        
        // Fetch All Data needed for menu
        const [clustersRes, categoriesRes, subcategoriesRes] = await Promise.all([
          postData('category-cluster/list', { limit: 500 }, token, false),
          postData('category/list', { limit: 1000 }, token, false),
          postData('subcategory/list', { limit: 2000 }, token, false)
        ]);

        const allClusters = (clustersRes?.clusters || []).filter(c => c.status === "active");
        const allCategories = (categoriesRes?.categories || []).filter(c => c.status === "active");
        const allSubcategories = (subcategoriesRes?.subcategories || []).filter(s => s.status === "active");

        setClusters(allClusters.sort((a,b) => (a.position || 0) - (b.position || 0)));
        setCategories(allCategories);
        setSubcategories(allSubcategories);

        // Identify categories not part of any cluster
        const unclustered = allCategories.filter(cat => {
          const clusterId = typeof cat.categoryCluster === 'object' ? cat.categoryCluster?._id : cat.categoryCluster;
          return !clusterId;
        });
        setUnclusteredCategories(unclustered);
      } catch (error) {
        console.error("Error fetching Navbar data:", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const storedUser = getUser();
    const token = getToken();
    if (token) {
      setIsAuthenticated(true);
      setUser(storedUser);
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
  }, [location]);


  const getCategoriesForCluster = (clusterId) => {
    if (!clusterId) return [];
    const cidStr = clusterId.toString();
    return categories.filter((cat) => {
      const catCid = cat.categoryCluster?._id || cat.categoryCluster;
      return catCid?.toString() === cidStr;
    });
  };

  const getSubcategoriesForCategory = (categoryId) => {
    if (!categoryId) return [];
    const catIdStr = categoryId.toString();
    return subcategories.filter((sub) => {
      const subCatId = sub.category_id?._id || sub.category_id;
      return subCatId?.toString() === catIdStr;
    });
  };

  const handleSubcategoryClick = (subcategory, category) => {
    const categorySlug = category?.slug || category?.title?.toLowerCase().replace(/\s+/g, '-') || 'category';
    const subcategorySlug = subcategory?.slug || subcategory?.title?.toLowerCase().replace(/\s+/g, '-') || 'subcategory';
    
    navigate(`/${categorySlug}/${subcategorySlug}`, {
      state: {
        categoryId: category?._id,
        subcategoryId: subcategory?._id,
        categoryTitle: category?.title,
        subcategoryTitle: subcategory?.title
      }
    });
    setMobileOpen(false);
    setOpenDropdown(null);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.trim().length >= 3) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
      setSearchSuggestions([]);
    }
  };

  // Debounced Search API call
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchTerm.trim().length < 3) {
        setSearchSuggestions([]);
        return;
      }
      setIsSearching(true);
      try {
        const tokenValue = getToken();
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await postData(`product/list?search=${encodeURIComponent(searchTerm)}&limit=6`, {}, token);
        const results = response.results || response.products || [];
        setSearchSuggestions(results);
      } catch (error) {
        console.error("Error fetching search suggestions:", error);
      } finally {
        setIsSearching(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchSuggestions();
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      setMobileSearchOpen(false);
      setShowSuggestions(false);
      if (searchTerm.trim().length >= 3) {
        navigate(`/search?searchTerm=${encodeURIComponent(searchTerm)}`);
      }
    }
  };

  const handleSuggestionClick = (product) => {
    setShowSuggestions(false);
    setSearchTerm(product.title || "");
    navigate(`/product/${product._id}`);
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout? You will be logged out of your account!")) {
      logout();
      setUser(null);
      setIsAuthenticated(false);
      window.location.href = "/";
    }
  };

  if (["/signup", "/signin", "/business/signin", "/business/signup"].includes(location.pathname)) return null;

  return (
    <>
      <AppBar position="sticky" sx={{ bgcolor: "white", color: "#111", boxShadow: "0 2px 15px rgba(0,0,0,0.05)", zIndex: 1200 }}>
        {/* TOP BAR */}
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: "space-between", height: { xs: 60, md: 90 }, px: "0 !important", gap: 2 }}>
            <Box component="img" src="/img/tvfmlogo1.png" alt="logo" onClick={() => navigate("/")} sx={{ height: { xs: 55, md: 80 }, cursor: "pointer" }} />

            <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
              <Box sx={{ display: { xs: "none", md: "flex" }, flexGrow: 1, maxWidth: 600, position: "relative" }}>
                <Box sx={{ display: "flex", width: "100%", bgcolor: "#f5f5f5", borderRadius: "8px", px: 2, alignItems: "center" }}>
                  <SearchIcon sx={{ color: "#888", mr: 1 }} />
                  <InputBase 
                    placeholder="Search products..." 
                    sx={{ flex: 1, height: 45, fontSize: "14px", ...rubikfont }} 
                    value={searchTerm} 
                    onChange={handleInputChange} 
                    onKeyDown={handleSearchKeyPress}
                    onFocus={() => {
                      if (searchTerm.trim().length >= 3) setShowSuggestions(true);
                    }}
                  />
                </Box>
                
                {/* Search Suggestions Dropdown */}
                {showSuggestions && (
                  <Paper 
                    elevation={3}
                    sx={{
                      position: "absolute",
                      top: "50px",
                      left: 0,
                      right: 0,
                      bgcolor: "white",
                      zIndex: 1300,
                      borderRadius: 2,
                      maxHeight: "400px",
                      overflowY: "auto",
                      boxShadow: "0px 8px 24px rgba(0,0,0,0.12)"
                    }}
                  >
                    {isSearching ? (
                      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress size={24} sx={{ color: "#C8A054" }} />
                      </Box>
                    ) : searchSuggestions.length > 0 ? (
                      <List sx={{ p: 0 }}>
                        {searchSuggestions.map((product) => (
                          <ListItem 
                            key={product._id} 
                            disablePadding 
                            onClick={() => handleSuggestionClick(product)}
                            sx={{
                              borderBottom: "1px solid #f0f0f0",
                              '&:hover': { bgcolor: '#f9f9f9' }
                            }}
                          >
                            <ListItemButton sx={{ gap: 2 }}>
                              <Box 
                                component="img" 
                                src={product.product_images?.[0] || "https://via.placeholder.com/50"} 
                                sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1 }} 
                              />
                              <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                                <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#333", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", ...rubikfont }}>
                                  {product.title}
                                </Typography>
                                <Typography sx={{ fontSize: "12px", color: "#C8A054", fontWeight: 600 }}>
                                  ₹{product.b2b_sellingPrice || product.b2c_sellingPrice || 0}
                                </Typography>
                              </Box>
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box sx={{ p: 3, textAlign: "center" }}>
                        <Typography sx={{ fontSize: "14px", color: "#666", ...rubikfont }}>No products found.</Typography>
                      </Box>
                    )}
                  </Paper>
                )}
              </Box>
            </ClickAwayListener>

            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, md: 3 } }}>
              <IconButton sx={{ display: { md: "none" } }} onClick={() => setMobileSearchOpen(true)}><SearchIcon /></IconButton>
              
              <Link to="/wishlist" style={{ textDecoration: "none", color: "#C8A054", textAlign: "center" }}>
                <FavoriteBorderIcon sx={{ fontSize: 26, color: "#C8A054" }} />
                <Typography sx={{ fontSize: "11px", fontWeight: 500, mt: -0.5, display: { xs: "none", sm: "block" } }}>Wishlist</Typography>
              </Link>

              <Link to="/addtocart" style={{ textDecoration: "none", color: "#C8A054", textAlign: "center", position: "relative" }}>
                <ShoppingCartIcon sx={{ fontSize: 26, color: "#C8A054" }} />
                {cartCount > 0 && <Box sx={{ position: "absolute", top: -8, right: -10, bgcolor: "#C8A054", color: "white", borderRadius: "50%", width: 18, height: 18, fontSize: "10px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 500 }}>{cartCount}</Box>}
                <Typography sx={{ fontSize: "11px", fontWeight: 500, mt: -0.5, display: { xs: "none", sm: "block" } }}>Bag</Typography>
              </Link>

              {!isAuthenticated ? (
                <Link to="/signin" style={{ textDecoration: "none", color: "#C8A054", textAlign: "center" }}>
                  <AccountCircleIcon sx={{ fontSize: 26, color: "#C8A054" }} />
                  <Typography sx={{ fontSize: "11px", fontWeight: 500, mt: -0.5, display: { xs: "none", sm: "block" } }}>Sign In</Typography>
                </Link>
              ) : (
                <Box onClick={(e) => setProfileAnchorEl(e.currentTarget)} sx={{ cursor: "pointer", textAlign: "center", color: "#C8A054" }}>
                  <Avatar sx={{ width: 26, height: 26, bgcolor: "#C8A054", color: "white", fontSize: "12px", mx: "auto" }}>{user?.name?.charAt(0).toUpperCase()}</Avatar>
                  <Typography sx={{ fontSize: "11px", fontWeight: 500, mt: 0.3, display: { xs: "none", sm: "block" } }}>Account</Typography>
                </Box>
              )}

              <IconButton sx={{ display: { md: "none" } }} onClick={() => setMobileOpen(true)}><MenuIcon /></IconButton>
            </Box>
          </Toolbar>
        </Container>

        <Box sx={{ display: { xs: "none", md: "block" }, borderTop: "1px solid #f0f0f0" }}>
          <Container maxWidth="xl" sx={{ display: "flex", justifyContent: "center" }}>
            <Box sx={{ display: "flex", gap: 4, py: 1, position: "relative" }}>
              {clusters.map((cluster) => (
                <Box 
                  key={cluster._id} 
                  onMouseEnter={() => handleMouseEnter(cluster._id)} 
                  onMouseLeave={handleMouseLeave}
                  sx={{ 
                    cursor: "pointer", 
                    display: "flex", 
                    alignItems: "center", 
                    gap: 0.5,
                    pb: 0.5,
                    transition: "all 0.2s"
                  }}
                >
                  <Typography sx={{ fontSize: "13px", fontWeight: 500, letterSpacing: "1px", textTransform: "uppercase", color: openDropdown === cluster._id ? "#C8A054" : "#111" }}>
                    {cluster.name}
                  </Typography>
                  <ExpandMoreIcon sx={{ fontSize: 16, color: openDropdown === cluster._id ? "#C8A054" : "#888", transform: openDropdown === cluster._id ? "rotate(180deg)" : "none", transition: "0.2s" }} />
                </Box>
              ))}
              {unclusteredCategories.length > 0 && (
                <Box 
                  onMouseEnter={() => handleMouseEnter("others")} 
                  onMouseLeave={handleMouseLeave}
                  sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, pb: 0.5 }}
                >
                  <Typography sx={{ fontSize: "13px", fontWeight: 500, textTransform: "uppercase", color: openDropdown === "others" ? "#C8A054" : "#111" }}>OTHERS</Typography>
                  <ExpandMoreIcon sx={{ fontSize: 16, color: openDropdown === "others" ? "#C8A054" : "#888" }} />
                </Box>
              )}

              {/* MEGA MENU CONTAINER - FIXED 70% HEIGHT + HORIZONTAL WRAP */}
              {openDropdown && (
                <Box 
                  onMouseEnter={() => { if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); }}
                  onMouseLeave={handleMouseLeave}
                  sx={{ 
                    position: "absolute", 
                    top: "100%", 
                    left: "50%",
                    transform: "translateX(-50%)",
                    height: "70vh",
                    width: "90vw",
                    overflowX: "auto",
                    overflowY: "hidden",
                    bgcolor: "white", 
                    boxShadow: "0 25px 50px rgba(0,0,0,0.15)", 
                    p: 6, 
                    zIndex: 10, 
                    border: "1px solid #f0f0f0",
                    borderTop: "none",
                    borderRadius: "0 0 32px 32px",
                    animation: "menuSlideIn 0.2s ease"
                  }}
                >
                  <Box sx={{ 
                    columnWidth: "220px", 
                    columnGap: 4, 
                    height: "calc(70vh - 96px)", 
                    width: "100%",
                  }}>
                    {(openDropdown === "others" ? unclusteredCategories : getCategoriesForCluster(openDropdown)).map((cat) => (
                      <Box key={cat._id} sx={{ mb: 4 }}>
                        <Typography 
                          onClick={() => {
                            const catSlug = cat.slug || cat.title?.toLowerCase().replace(/\s+/g, '-') || 'category';
                            navigate(`/${catSlug}`, { state: { categoryId: cat._id, categoryTitle: cat.title } });
                          }}
                          sx={{ fontSize: "14px", fontWeight: 500, color: "#111", mb: 2, cursor: "pointer", "&:hover": { color: "#C8A054" } }}
                        >
                          {cat.title}
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                          {getSubcategoriesForCategory(cat._id).map((sub) => (
                            <Typography 
                              key={sub._id} 
                              onClick={() => handleSubcategoryClick(sub, cat)}
                              sx={{ fontSize: "13px", color: "#666", cursor: "pointer", "&:hover": { color: "#C8A054", pl: 0.5 }, transition: "0.2s" }}
                            >
                              {sub.title}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Container>
        </Box>
      </AppBar>

      {/* MOBILE DRAWER */}
      <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)} PaperProps={{ sx: { width: "85%", maxWidth: 320 } }}>
        <Box sx={{ p: 2, borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box component="img" src="/img/tvfmlogo1.png" onClick={() => navigate("/")} sx={{ height: 55, cursor: "pointer" }} />
          <IconButton onClick={() => setMobileOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <List sx={{ pt: 0 }}>
          {clusters.map((cluster) => (
            <React.Fragment key={cluster._id}>
              <ListItemButton onClick={() => setActiveMobileCluster(activeMobileCluster === cluster._id ? null : cluster._id)} sx={{ py: 2, borderBottom: "1px solid #f9f9f9" }}>
                <ListItemText primary={cluster.name.toUpperCase()} primaryTypographyProps={{ fontSize: "14px", fontWeight: 500 }} />
                {activeMobileCluster === cluster._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
              <Collapse in={activeMobileCluster === cluster._id}>
                {getCategoriesForCluster(cluster._id).map((cat) => (
                  <React.Fragment key={cat._id}>
                    <ListItemButton sx={{ pl: 4, bgcolor: "#fafafa" }} onClick={() => setActiveMobileCategory(activeMobileCategory === cat._id ? null : cat._id)}>
                      <ListItemText primary={cat.title} primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }} />
                      {activeMobileCategory === cat._id ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                    </ListItemButton>
                    <Collapse in={activeMobileCategory === cat._id}>
                      {getSubcategoriesForCategory(cat._id).map((sub) => (
                        <ListItemButton key={sub._id} sx={{ pl: 7 }} onClick={() => handleSubcategoryClick(sub, cat)}>
                          <ListItemText primary={sub.title} primaryTypographyProps={{ fontSize: "12px", color: "#555" }} />
                        </ListItemButton>
                      ))}
                    </Collapse>
                  </React.Fragment>
                ))}
              </Collapse>
            </React.Fragment>
          ))}
          {/* Unclustered for Mobile */}
          {unclusteredCategories.length > 0 && (
            <ListItemButton onClick={() => setActiveMobileCluster(activeMobileCluster === 'others' ? null : 'others')}>
              <ListItemText primary="OTHERS" primaryTypographyProps={{ fontSize: "14px", fontWeight: 500 }} />
              {activeMobileCluster === 'others' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>
          )}
          <ListItemButton onClick={() => { setMobileOpen(false); navigate("/faqs"); }} sx={{ py: 2, borderBottom: "1px solid #f9f9f9" }}>
            <ListItemText primary="HELP & FAQS" primaryTypographyProps={{ fontSize: "14px", fontWeight: 500 }} />
          </ListItemButton>
          <ListItemButton onClick={() => { setMobileOpen(false); navigate("/support"); }} sx={{ py: 2, borderBottom: "1px solid #f9f9f9" }}>
            <ListItemText primary="SUPPORT TICKET" primaryTypographyProps={{ fontSize: "14px", fontWeight: 500 }} />
          </ListItemButton>
        </List>
      </Drawer>

      {/* MOBILE SEARCH OVERLAY */}
      <Slide direction="down" in={mobileSearchOpen}>
        <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, bgcolor: "white", zIndex: 2000, p: 2, boxShadow: "0 5px 20px rgba(0,0,0,0.1)", display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InputBase 
              autoFocus 
              placeholder="Search products..." 
              value={searchTerm} 
              onChange={handleInputChange} 
              onKeyDown={handleSearchKeyPress} 
              sx={{ flex: 1, height: 45, border: "1px solid #eee", px: 2, borderRadius: 1 }} 
            />
            <IconButton onClick={() => {
              setMobileSearchOpen(false);
              setShowSuggestions(false);
            }}><CloseIcon /></IconButton>
          </Box>
          
          {/* Mobile Search Suggestions */}
          {showSuggestions && (
            <Box sx={{ maxHeight: "60vh", overflowY: "auto", mt: 1 }}>
              {isSearching ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress size={24} sx={{ color: "#C8A054" }} />
                </Box>
              ) : searchSuggestions.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {searchSuggestions.map((product) => (
                    <ListItem 
                      key={product._id} 
                      disablePadding 
                      onClick={() => {
                        setMobileSearchOpen(false);
                        handleSuggestionClick(product);
                      }}
                      sx={{ borderBottom: "1px solid #f0f0f0" }}
                    >
                      <ListItemButton sx={{ gap: 2, px: 1 }}>
                        <Box 
                          component="img" 
                          src={product.product_images?.[0] || "https://via.placeholder.com/50"} 
                          sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1 }} 
                        />
                        <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#333", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", ...rubikfont }}>
                            {product.title}
                          </Typography>
                          <Typography sx={{ fontSize: "12px", color: "#C8A054", fontWeight: 600 }}>
                            ₹{product.b2b_sellingPrice || product.b2c_sellingPrice || 0}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "14px", color: "#666", ...rubikfont }}>No products found.</Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Slide>

      {/* PROFILE DROPDOWN MENU */}
      <Menu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={() => setProfileAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 180,
            boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
            borderRadius: "12px",
            border: "1px solid #f0f0f0",
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#111", ...rubikfont }}>{user?.name}</Typography>
          <Typography sx={{ fontSize: "11px", color: "#888", ...rubikfont }}>{user?.email}</Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem 
          onClick={() => { setProfileAnchorEl(null); navigate("/profile"); }}
          sx={{ fontSize: "14px", fontWeight: 500, py: 1.2, gap: 1.5, ...rubikfont }}
        >
          <AccountCircleIcon sx={{ fontSize: 20, color: "#888" }} /> Profile
        </MenuItem>
        <MenuItem 
          onClick={() => { setProfileAnchorEl(null); navigate("/orders"); }}
          sx={{ fontSize: "14px", fontWeight: 500, py: 1.2, gap: 1.5, ...rubikfont }}
        >
          <FavoriteBorderIcon sx={{ fontSize: 20, color: "#888" }} /> My Orders
        </MenuItem>
        <MenuItem 
          onClick={() => { setProfileAnchorEl(null); navigate("/faqs"); }}
          sx={{ fontSize: "14px", fontWeight: 500, py: 1.2, gap: 1.5, ...rubikfont }}
        >
          <AccountCircleIcon sx={{ fontSize: 20, color: "#888" }} /> Help & FAQs
        </MenuItem>
        <MenuItem 
          onClick={() => { setProfileAnchorEl(null); navigate("/support"); }}
          sx={{ fontSize: "14px", fontWeight: 500, py: 1.2, gap: 1.5, ...rubikfont }}
        >
          <AccountCircleIcon sx={{ fontSize: 20, color: "#888" }} /> Support Ticket
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem 
          onClick={handleLogout}
          sx={{ fontSize: "14px", fontWeight: 500, py: 1.2, gap: 1.5, color: "#d32f2f", ...rubikfont }}
        >
          Logout
        </MenuItem>
      </Menu>

      <style>{`
        @keyframes menuSlideIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </>
  );
}

const WrappingFlexContainer = ({ children }) => (
  <Box sx={{ 
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
    height: "calc(70vh - 64px)", 
    gap: 0, 
    width: "100%",
    alignContent: "flex-start"
  }}>
    {children}
  </Box>
);

export default Navbar;
